import { POST } from './api'
import { ReadObject } from './storage'
import { getProductAreaFromPath, isLitePath } from 'utils/portal'

const GA_MEASUREMENT_ID = 'G-LZPVM5JMFH'
const SESSION_STORAGE_KEY = 'mvu.analytics.sessionId'
const SESSION_STARTED_KEY = 'mvu.analytics.sessionStarted'
const recentEventKeys = new Map()

const INTERNAL_EVENT_TYPES = {
  login: 'LOGIN',
  logout: 'LOGOUT',
  registration_start: 'SIGNUP_START',
  registration_complete: 'SIGNUP',
  profile_update: 'PROFILE_UPDATE',
  password_reset: 'PASSWORD_RESET',
  page_view: 'PAGE_VIEW',
  page_exit: 'PAGE_EXIT',
  session_start: 'SESSION_START',
  session_end: 'SESSION_END',
  locked_content_click: 'LOCKED_CONTENT_CLICK',
  course_view: 'COURSE_VIEW',
  course_start: 'COURSE_START',
  course_progress: 'COURSE_PROGRESS',
  course_complete: 'COURSE_COMPLETE',
  survey_viewed: 'SURVEY_VIEW',
  survey_started: 'SURVEY_START',
  survey_submitted: 'SURVEY_SUBMITTED',
  survey_validation_failed: 'SURVEY_VALIDATION_FAILED',
  survey_required_before_quiz: 'SURVEY_REQUIRED_BEFORE_QUIZ',
  survey_end_prompt_viewed: 'SURVEY_END_PROMPT_VIEW',
  survey_end_prompt_opened: 'SURVEY_END_PROMPT_OPEN',
  survey_end_prompt_dismissed: 'SURVEY_END_PROMPT_DISMISS',
  competitor_feedback_prompt_shown: 'COMPETITOR_FEEDBACK_PROMPT_SHOWN',
  competitor_feedback_submitted: 'COMPETITOR_FEEDBACK_SUBMITTED',
  competitor_feedback_issue_created: 'COMPETITOR_FEEDBACK_ISSUE_CREATED',
  competitor_feedback_failed: 'COMPETITOR_FEEDBACK_FAILED',
  partners_and_staff_view: 'CONTENT_VIEW',
  partners_and_staff_start: 'CONTENT_VIEW',
  partners_and_staff_complete: 'CONTENT_VIEW',
  file_view: 'FILE_VIEW',
  file_download: 'FILE_DOWNLOAD',
  pdf_open: 'PDF_OPEN',
  document_open: 'DOCUMENT_OPEN',
  search: 'SEARCH',
  search_no_results: 'SEARCH_NO_RESULTS',
  search_result_click: 'SEARCH_RESULT_CLICK',
  content_favorite: 'CONTENT_FAVORITE',
  external_link_click: 'EXTERNAL_LINK_CLICK',
  competitive_intelligence_opened: 'COMPETITIVE_HUB_OPENED',
  competitive_intelligence_overview_loaded: 'COMPETITIVE_SECTION_VIEWED',
  competitive_intelligence_research_viewed: 'COMPETITIVE_SECTION_VIEWED',
  competitive_intelligence_market_viewed: 'COMPETITIVE_SECTION_VIEWED',
  competitive_intelligence_assistants_viewed: 'COMPETITIVE_SECTION_VIEWED',
  competitive_intelligence_research_record_opened: 'COMPETITIVE_CONTENT_VIEWED',
  competitive_intelligence_research_original_source_opened: 'COMPETITIVE_CONTENT_SOURCE_OPENED',
  competitive_intelligence_research_searched: 'COMPETITIVE_SEARCH_PERFORMED',
  competitive_intelligence_research_source_filtered: 'COMPETITIVE_FILTER_APPLIED',
  competitive_intelligence_load_failed: 'COMPETITIVE_CONTENT_ENGAGED',
  competitive_intelligence_research_load_failed: 'COMPETITIVE_CONTENT_ENGAGED',
  competitive_intelligence_market_load_failed: 'COMPETITIVE_CONTENT_ENGAGED',
  competitive_ai_assistant_opened: 'COMPETITIVE_AI_ASSISTANT_OPENED',
  competitive_ai_prompt_sent: 'COMPETITIVE_AI_PROMPT_SENT',
  competitive_ai_source_opened: 'COMPETITIVE_AI_SOURCE_OPENED',
  competitive_ai_feedback_submitted: 'COMPETITIVE_AI_FEEDBACK_SUBMITTED',
}

const ENDPOINT_BY_PREFIX = {
  SEARCH: '/engagement/search',
  FILE: '/engagement/download',
  PDF: '/engagement/download',
  DOCUMENT: '/engagement/download',
  PRODUCT_BROCHURE: '/engagement/download',
  CERTIFICATE_DOWNLOAD: '/engagement/download',
  REPEATED_RESOURCE_DOWNLOAD: '/engagement/download',
  VIDEO: '/engagement/video',
  COURSE: '/engagement/course',
  LESSON: '/engagement/course',
  QUIZ: '/engagement/course',
  CERTIFICATE: '/engagement/course',
  CERTIFICATION: '/engagement/course',
  SURVEY: '/engagement/course',
}

function normalizeStr(value) {
  return String(value || '').trim().toLowerCase()
}

function randomId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `evt-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function safeSessionStorageGet(key) {
  try {
    return window.sessionStorage.getItem(key)
  } catch (_error) {
    return null
  }
}

function safeSessionStorageSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value)
  } catch (_error) {
    // Analytics must never block navigation when storage is unavailable.
  }
}

export function getAnalyticsSessionId() {
  if (typeof window === 'undefined') return null
  const existing = safeSessionStorageGet(SESSION_STORAGE_KEY)
  if (existing) return existing
  const sessionId = randomId()
  safeSessionStorageSet(SESSION_STORAGE_KEY, sessionId)
  return sessionId
}

function getAreaFromPath(pathname) {
  const parts = String(pathname || '').split('/').filter(Boolean)
  const root = parts[0]

  if (root === 'mvu-accelerator') return 'mvu_accelerator'
  if (root === 'mvu-lite' || root === 'mvu-essentials') return 'mvu_essentials'
  if (root === 'dashboard') return 'dashboard'
  if (root === 'favorites') return 'favorites'
  if (root === 'faq') return 'faq'

  let type = ''
  if (root === 'sub-category') type = parts.length >= 2 ? parts[parts.length - 1] : ''
  if (root === 'mode') type = parts.length >= 2 ? parts[1] : ''

  const normalizedType = normalizeStr(type)
  if (['elearning', 'e-learning', 'learning', 'course', 'courses'].includes(normalizedType)) return 'training_learning'
  if (['partners-and-staffs', 'partners-and-staff', 'partners_staff', 'partnersandstaff'].includes(normalizedType)) return 'partners_and_staff'
  if (['marketing', 'marketing-materials', 'marketing-material'].includes(normalizedType)) return 'marketing_materials'
  if (['support-materials'].includes(normalizedType)) return 'practice_support_materials'
  if (['product-informations', 'product-information'].includes(normalizedType)) return 'product_information'
  return 'other'
}

function activeAuthentication() {
  if (typeof window === 'undefined') return null
  const pathname = window.location?.pathname || ''
  if (isLitePath(pathname)) {
    return ReadObject('mvu_essentials')?.jwt ? ReadObject('mvu_essentials') : ReadObject('authentication')
  }
  if (pathname.startsWith('/mvu-accelerator')) {
    return ReadObject('mvu_accelerator')?.jwt ? ReadObject('mvu_accelerator') : ReadObject('authentication')
  }
  return ReadObject('authentication')
}

export function resolveInternalEventType(eventName) {
  const normalized = String(eventName || '').trim()
  if (/^[A-Z][A-Z0-9_]+$/.test(normalized)) return normalized

  const lowerName = normalizeStr(normalized)
  if (INTERNAL_EVENT_TYPES[lowerName]) return INTERNAL_EVENT_TYPES[lowerName]
  if (lowerName.endsWith('_video_started')) return 'VIDEO_START'
  if (lowerName.endsWith('_video_progress')) return 'VIDEO_PROGRESS'
  if (lowerName.endsWith('_video_completed')) return 'VIDEO_COMPLETE'
  if (lowerName.endsWith('_video_closed')) return 'VIDEO_EXIT'
  return null
}

export function resolveEngagementEndpoint(eventType) {
  const match = Object.entries(ENDPOINT_BY_PREFIX).find(([prefix]) => (
    eventType === prefix || eventType.startsWith(`${prefix}_`)
  ))
  return match?.[1] || '/engagement/events'
}

function detectBrowser(userAgent = '') {
  if (/Edg\//i.test(userAgent)) return 'Edge'
  if (/OPR\//i.test(userAgent)) return 'Opera'
  if (/Chrome\//i.test(userAgent)) return 'Chrome'
  if (/Firefox\//i.test(userAgent)) return 'Firefox'
  if (/Safari\//i.test(userAgent)) return 'Safari'
  return 'Other'
}

function detectDevice(userAgent = '') {
  if (/tablet|ipad/i.test(userAgent)) return 'tablet'
  if (/mobile|iphone|android/i.test(userAgent)) return 'mobile'
  return 'desktop'
}

function compactObject(value) {
  return Object.entries(value).reduce((acc, [key, entry]) => {
    if (entry !== undefined && entry !== null && entry !== '') acc[key] = entry
    return acc
  }, {})
}

export function buildInternalEventPayload(eventName, params = {}) {
  const eventType = resolveInternalEventType(eventName)
  if (!eventType || typeof window === 'undefined') return null

  const navigatorObject = window.navigator || {}
  const requestedPath = String(params.page_path || window.location?.pathname || '/').split('?')[0].split('#')[0]
  const safePath = requestedPath.startsWith('/') ? requestedPath : `/${requestedPath}`
  const metadata = compactObject({
    ...params,
    page_path: safePath,
    ga_event_name: eventName,
    area_name: getAreaFromPath(safePath),
  })
  if (eventType.startsWith('COMPETITIVE_')) {
    delete metadata.query
    delete metadata.search_term
  }

  return compactObject({
    eventId: params.event_id || randomId(),
    sessionId: getAnalyticsSessionId(),
    eventType,
    productArea: getProductAreaFromPath(safePath),
    eventAction: normalizeStr(eventName).replace(/[^a-z0-9_]+/g, '_'),
    eventLabel: params.event_label || params.content_name || params.video_name,
    pageUrl: `${window.location?.origin || ''}${safePath}`,
    pagePath: safePath,
    pageTitle: params.page_title || window.document?.title,
    language: params.language || window.document?.documentElement?.lang || navigatorObject.language,
    country: params.country,
    device: params.device || detectDevice(navigatorObject.userAgent),
    platform: params.platform || navigatorObject.userAgentData?.platform || navigatorObject.platform,
    browser: params.browser || detectBrowser(navigatorObject.userAgent),
    referrer: params.referrer || window.document?.referrer,
    searchTerm: eventType.startsWith('COMPETITIVE_') ? undefined : params.search_term || params.query,
    contentType: params.content_type || params.video_type,
    contentId: params.content_id || params.lesson_id,
    competitiveSection: params.competitive_section || params.section,
    competitorId: params.competitor_id || params.competitor,
    competitiveSource: params.competitive_source || params.source,
    assistantId: params.assistant_id,
    searchId: params.search_id,
    resultPosition: params.result_position,
    resultCount: params.result_count ?? params.results_count,
    filterType: params.filter_type,
    filterValue: params.filter_value,
    courseId: params.course_id,
    videoId: params.video_id || params.media_id,
    resourceId: params.resource_id || params.document_id || params.file_id,
    durationSeconds: params.duration_seconds,
    completionPercentage: params.progress_percent,
    resultsCount: params.results_count,
    clickedResult: params.clicked_result,
    hadResults: params.had_results,
    metadata,
    timestamp: new Date().toISOString(),
  })
}

async function sendInternalPayload(payload, options = {}) {
  if (!activeAuthentication()?.jwt) return null
  if (!payload) return null

  try {
    const endpoint = resolveEngagementEndpoint(payload.eventType)
    return Object.keys(options).length
      ? await POST(endpoint, payload, true, options)
      : await POST(endpoint, payload, true)
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] Internal event delivery failed.', error)
    }
    return null
  }
}

function sendGaEvent(eventName, params = {}) {
  if (process.env.REACT_APP_DEPLOYMENT_ENV === 'staging' || typeof window === 'undefined' || !window.gtag) return
  window.gtag('event', eventName, {
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
    area_name: getAreaFromPath(window.location.pathname),
    ...params,
    send_to: GA_MEASUREMENT_ID,
  })
}

export function trackPageView(path) {
  if (typeof window === 'undefined') return Promise.resolve(null)
  const payload = buildInternalEventPayload('PAGE_VIEW', { page_path: path })

  if (process.env.REACT_APP_DEPLOYMENT_ENV !== 'staging' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      area_name: getAreaFromPath(window.location.pathname),
      event_id: payload?.eventId,
      session_id: payload?.sessionId,
      send_to: GA_MEASUREMENT_ID,
    })
  }

  return sendInternalPayload(payload)
}

export function trackEvent(eventName, params = {}) {
  const payload = buildInternalEventPayload(eventName, params)
  sendGaEvent(eventName, {
    ...params,
    ...(payload?.eventId ? { event_id: payload.eventId } : {}),
    ...(payload?.sessionId ? { session_id: payload.sessionId } : {}),
  })
  return sendInternalPayload(payload)
}

export function trackEventOnce(eventName, params = {}, dedupeKey, ttlMs = 1500) {
  const key = `${resolveInternalEventType(eventName) || eventName}:${dedupeKey || JSON.stringify(params)}`
  const now = Date.now()
  const previous = recentEventKeys.get(key)
  if (previous && now - previous < ttlMs) return Promise.resolve(null)
  recentEventKeys.set(key, now)
  if (recentEventKeys.size > 500) {
    for (const [storedKey, timestamp] of recentEventKeys) {
      if (now - timestamp >= ttlMs) recentEventKeys.delete(storedKey)
    }
  }
  return trackEvent(eventName, params)
}

export function trackSessionStart() {
  if (typeof window === 'undefined' || safeSessionStorageGet(SESSION_STARTED_KEY)) return Promise.resolve(null)
  if (!activeAuthentication()?.jwt) return Promise.resolve(null)
  safeSessionStorageSet(SESSION_STARTED_KEY, 'true')
  return trackEvent('SESSION_START')
}

export function trackSessionEnd(params = {}) {
  if (typeof window === 'undefined' || !safeSessionStorageGet(SESSION_STARTED_KEY)) return Promise.resolve(null)
  const payload = buildInternalEventPayload('SESSION_END', params)
  sendGaEvent('SESSION_END', {
    ...params,
    ...(payload?.eventId ? { event_id: payload.eventId } : {}),
    ...(payload?.sessionId ? { session_id: payload.sessionId } : {}),
  })
  return sendInternalPayload(payload, { keepalive: true })
}
